import { Injectable, OnDestroy, signal, computed } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

export interface PageHeaderConfig {
  title?: string;
  actions?: ActionButton[] | null;
  showBackButton?: boolean;
  backTo?: string;
}

export interface ActionButton {
  label: string;
  icon?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  hideLabel?: boolean;
  subActions?: {
    label: string;
    icon?: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'danger';
    disabled?: boolean;
  }[];
}

@Injectable({
  providedIn: 'root'
})
export class PageHeaderService implements OnDestroy {
  private config = signal<PageHeaderConfig>({});

  readonly title = computed(() => this.config().title ?? '');
  readonly actions = computed(() => this.config().actions ?? []);
  readonly showBackButton = computed(() => this.config().showBackButton ?? false);
  readonly backTo = computed(() => this.config().backTo ?? '');

  constructor(private router: Router) {
    // Auto-reset op route changes
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => this.reset());
  }

  updateConfig(newConfig: Partial<PageHeaderConfig>) {
    this.config.update(current => ({
      ...current,
      ...newConfig
    }));
  }

  addAction(action: ActionButton) {
    this.config.update(current => ({
      ...current,
      actions: [...(current.actions || []), action]
    }));
  }

  removeAction(label: string) {
    this.config.update(current => ({
      ...current,
      actions: (current.actions || []).filter(action => action.label !== label)
    }));
  }

  clearActions() {
    this.config.update(current => ({ ...current, actions: [] }));
  }

  reset() {
    this.config.set({});
  }

  ngOnDestroy() {
    // Cleanup niet meer nodig voor signals
  }
}
